import React, { useRef } from "react";
import { Paperclip, X } from "react-feather";
import toast from "react-hot-toast";

// Product brochures to email alongside the quotation (2026-09-09).
//
// PASS-THROUGH ONLY. These files are posted with the send request, attached to
// the outgoing email and then discarded - nothing is stored in the database or
// anywhere else, by explicit instruction. Two things follow, and the panel says
// both out loud rather than letting the user discover them:
//   * they apply to THIS send only;
//   * a later Resend carries the quotation on its own.
//
// Limits mirror BrochureAttachmentReader on the server exactly, so the user is
// told here rather than by a rejected upload after waiting for the send.
export const MAX_BROCHURES = 5;
export const MAX_BROCHURE_BYTES = 5 * 1024 * 1024;
export const MAX_TOTAL_BROCHURE_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ["application/pdf", "image/jpeg", "image/png"];

const prettySize = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const BrochurePicker = ({ files, onChange, disabled = false }) => {
  const inputRef = useRef(null);

  const handlePick = (event) => {
    const picked = Array.from(event.target.files || []);
    // Let the same file be re-picked after being removed.
    event.target.value = "";
    if (picked.length === 0) return;

    const next = [...files];
    for (const file of picked) {
      if (next.length >= MAX_BROCHURES) {
        toast.error(`You can attach at most ${MAX_BROCHURES} brochures.`);
        break;
      }
      if (!ACCEPTED.includes(file.type)) {
        toast.error(`"${file.name}" is not a PDF or an image.`);
        continue;
      }
      if (file.size > MAX_BROCHURE_BYTES) {
        toast.error(`"${file.name}" is larger than ${prettySize(MAX_BROCHURE_BYTES)}.`);
        continue;
      }
      if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
      if (next.reduce((sum, f) => sum + f.size, 0) + file.size > MAX_TOTAL_BROCHURE_BYTES) {
        toast.error(`That would take the brochures over ${prettySize(MAX_TOTAL_BROCHURE_BYTES)} in total.`);
        break;
      }
      next.push(file);
    }
    onChange(next);
  };

  const total = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="border rounded p-2 mb-2">
      <div className="d-flex justify-content-between align-items-center">
        <span className="fw-semibold" style={{ fontSize: 13 }}>
          <Paperclip size={14} className="me-1" />
          Brochures to send with this quotation
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={disabled || files.length >= MAX_BROCHURES}
          onClick={() => inputRef.current?.click()}
        >
          Add file
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png"
        className="d-none"
        onChange={handlePick}
      />

      {files.length === 0 ? (
        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
          Optional. PDF, JPG or PNG &mdash; up to {MAX_BROCHURES} files, {prettySize(MAX_TOTAL_BROCHURE_BYTES)} in total.
        </div>
      ) : (
        <>
          <ul className="list-unstyled mb-1 mt-2">
            {files.map((file) => (
              <li key={`${file.name}-${file.size}`} className="d-flex align-items-center justify-content-between py-1">
                <span className="text-truncate" style={{ fontSize: 12.5 }}>
                  {file.name} <span className="text-muted">&middot; {prettySize(file.size)}</span>
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-danger"
                  disabled={disabled}
                  title="Remove"
                  onClick={() => onChange(files.filter((f) => !(f.name === file.name && f.size === file.size)))}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
          <div className="text-muted" style={{ fontSize: 11.5 }}>
            {files.length} of {MAX_BROCHURES} &middot; {prettySize(total)} of {prettySize(MAX_TOTAL_BROCHURE_BYTES)}.
            These are emailed with the quotation but not saved, so a later Resend will send the quotation on its own.
          </div>
        </>
      )}
    </div>
  );
};

export default BrochurePicker;
